import type { Plan } from "~/types/entities/finance";

export type PlanId =
  | "free"
  | "starter"
  | "professional"
  | "business_ai"
  | "enterprise";

export type Feature =
  | "universal_inbox"
  | "knowledge_base_internal"
  | "calendar"
  | "ticket_types"
  | "audit_log"
  | "workflow_builder"
  | "releases"
  | "ola"
  | "advanced_sla"
  | "api_access"
  | "integrations"
  | "support_ticketing"
  | "brand_portal"
  | "ai_intelligence"
  | "multiple_integrations"
  | "advanced_analytics"
  | "compliance"
  | "developer"
  | "governance"
  | "security_policies"
  | "sso"
  | "custom_deployment";

export const PLAN_META: Record<
  PlanId,
  { label: string; color: string; badge: string }
> = {
  free: { label: "Free Trial", color: "#64748b", badge: "FREE" },
  starter: { label: "Starter", color: "#0ea5e9", badge: "STARTER" },
  professional: { label: "Professional", color: "#35A7BE", badge: "PRO" },
  business_ai: { label: "Business AI", color: "#8b5cf6", badge: "AI" },
  enterprise: { label: "Enterprise", color: "#0f172a", badge: "ENT" },
};

export function getPlanPriceByCurrency(plan: Plan, currency: string) {
  const normalizedCurrency = currency.trim().toUpperCase();
  return plan.prices.find(
    (price) =>
      price.currencyId.toUpperCase() === normalizedCurrency ||
      price.currencyName.toUpperCase() === normalizedCurrency,
  )?.amount;
}

const PLAN_FEATURES: Record<PlanId, Feature[] | ["*"]> = {
  free: ["calendar"],
  starter: [
    "universal_inbox",
    "knowledge_base_internal",
    "calendar",
    "ticket_types",
    "audit_log",
  ],
  professional: [
    "universal_inbox",
    "knowledge_base_internal",
    "calendar",
    "ticket_types",
    "audit_log",
    "workflow_builder",
    "releases",
    "ola",
    "advanced_sla",
    "api_access",
    "integrations",
    "support_ticketing",
    "brand_portal",
  ],
  business_ai: [
    "universal_inbox",
    "knowledge_base_internal",
    "calendar",
    "ticket_types",
    "audit_log",
    "workflow_builder",
    "releases",
    "ola",
    "advanced_sla",
    "api_access",
    "integrations",
    "multiple_integrations",
    "support_ticketing",
    "brand_portal",
    "ai_intelligence",
    "advanced_analytics",
    "compliance",
    "developer",
  ],
  enterprise: ["*"],
};

export const FEATURE_META: Record<
  Feature,
  { label: string; upgradeMin: PlanId }
> = {
  universal_inbox: { label: "Universal Inbox", upgradeMin: "starter" },
  knowledge_base_internal: {
    label: "Internal Knowledge Base",
    upgradeMin: "starter",
  },
  calendar: { label: "Calendar", upgradeMin: "free" },
  ticket_types: { label: "Ticket Types", upgradeMin: "starter" },
  audit_log: { label: "Audit Log", upgradeMin: "starter" },
  workflow_builder: { label: "Workflow Builder", upgradeMin: "professional" },
  releases: { label: "Releases", upgradeMin: "professional" },
  ola: { label: "OLA Management", upgradeMin: "professional" },
  advanced_sla: { label: "Advanced SLA", upgradeMin: "professional" },
  api_access: { label: "API Access", upgradeMin: "professional" },
  integrations: { label: "Integrations", upgradeMin: "professional" },
  support_ticketing: { label: "Support Ticketing", upgradeMin: "professional" },
  brand_portal: { label: "Brand Portal", upgradeMin: "professional" },
  ai_intelligence: { label: "AI Intelligence", upgradeMin: "business_ai" },
  multiple_integrations: {
    label: "Multi-platform Integrations",
    upgradeMin: "business_ai",
  },
  advanced_analytics: {
    label: "Advanced Analytics",
    upgradeMin: "business_ai",
  },
  compliance: { label: "Compliance", upgradeMin: "business_ai" },
  developer: { label: "Developer API", upgradeMin: "business_ai" },
  governance: { label: "Governance", upgradeMin: "enterprise" },
  security_policies: { label: "Security & Policies", upgradeMin: "enterprise" },
  sso: { label: "SSO / SAML", upgradeMin: "enterprise" },
  custom_deployment: { label: "Custom Deployment", upgradeMin: "enterprise" },
};

const PLAN_ORDER: PlanId[] = [
  "free",
  "starter",
  "professional",
  "business_ai",
  "enterprise",
];

export const useSubscriptionPlanStore = defineStore("subscriptionPlan", () => {
  const plansFetch = useAsyncData(
    "plans",
    async () => {
      try {
        const response = await useRequest<{ data: Plan[] }>(
          "mock/subscription/plans",
        );
        return response._data?.data ?? [];
      } catch (error) {
        handleError({ error });
        return [];
      }
    },
    { default: () => [] },
  );

  const plans = computed(() => plansFetch.data.value);
  const loadingPlans = computed(() => plansFetch.pending.value);

  const activePlan = asyncComputed(async () => {
    try {
      const response = await useRequest<{ data: Plan }>(
        "mock/subscription/plans/active",
      );
      return response._data?.data;
    } catch (error) {
      handleError({ error });
    }
  }, undefined);

  const storedPlan = import.meta.client
    ? (localStorage.getItem("syncpro_plan") as PlanId | null)
    : null;
  const currentPlan = ref<PlanId>(
    storedPlan && PLAN_ORDER.includes(storedPlan) ? storedPlan : "free",
  );

  function toPlanId(plan: Plan): PlanId | undefined {
    const candidate = plan.name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "") as PlanId;
    return PLAN_ORDER.includes(candidate) ? candidate : undefined;
  }

  watch(
    activePlan,
    (plan) => {
      const planId = plan && toPlanId(plan);
      if (planId) currentPlan.value = planId;
    },
    { immediate: true },
  );

  function setPlan(plan: PlanId) {
    currentPlan.value = plan;
    if (import.meta.client) localStorage.setItem("syncpro_plan", plan);
  }

  function hasFeature(feature: Feature): boolean {
    const apiFeatures = activePlan.value?.features;
    if (apiFeatures?.length) {
      const normalizedFeatures = apiFeatures.map((value) =>
        value
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_"),
      );
      return normalizedFeatures.includes(feature);
    }

    const features = PLAN_FEATURES[currentPlan.value];
    if (features[0] === "*") return true;
    return (features as Feature[]).includes(feature);
  }

  function planRank(plan: PlanId): number {
    return PLAN_ORDER.indexOf(plan);
  }

  const meta = computed(() => PLAN_META[currentPlan.value]);

  const availableCurrencies = computed(() => [
    ...new Set(
      plans.value.flatMap((plan) =>
        plan.prices.map((price) => price.currencyId.toUpperCase()),
      ),
    ),
  ]);

  function getPrice(plan: Plan, currency: string) {
    return getPlanPriceByCurrency(plan, currency);
  }

  return {
    plans,
    loadingPlans,
    activePlan,
    currentPlan,
    meta,
    availableCurrencies,
    setPlan,
    hasFeature,
    planRank,
    getPrice,
  };
});
